/**
 * spiceSessionsApi — transport + react-query hook for the client
 * record's SPICE tab (migration 0189).
 *
 *   GET /api/t/{slug}/v1/clients/{clientId}/spice-sessions
 *
 * House pattern: spiceVisitPlansApi.ts fetch semantics (raw fetch +
 * ApiError normalization; TanStack Query owns caching). Gated
 * read_clinical_record server-side — no new capability; the tab only
 * mounts when the record's own gate already passed.
 *
 * One row per SPICE-band note (session / declined / filed, read from the
 * note's own first line — api/_shared/repos/spiceSessions.ts's
 * classifySpiceSessionKind), with a confirmed spice_sessions row's facts
 * layered on when one exists for that note.
 */

import { useQuery } from '@tanstack/react-query';
import { ApiError } from './api';

export type SpiceEngagementLevel = 'low' | 'mixed' | 'good';
export type SpiceSessionKind = 'session' | 'declined' | 'filed';

export interface SpiceSessionActivityWire {
  /** The activity in the therapist's own words (60 chars or fewer),
   *  e.g. "banana sundae" — absent on rows confirmed before this field
   *  existed, in which case the session row falls back to `activity`. */
  what: string | null;
  activity: string;
  goal: string | null;
  response: string;
  modality: string | null;
}

export interface SpiceSessionWire {
  note_id: string;
  occurred_on: string; // 'YYYY-MM-DD'
  therapist_name: string | null;
  kind: SpiceSessionKind;
  /** The note's decrypted first line only. */
  first_line: string;
  hours: number | null;
  // Confirmed spice_sessions facts, present only once the note's
  // proposal is confirmed; absent otherwise (null / false / empty).
  mood: string | null;
  engagement_level: SpiceEngagementLevel | null;
  next_plan: string | null;
  raise_with_lead: boolean;
  activities: SpiceSessionActivityWire[];
}

export interface SpiceSessionsResponse {
  rows: SpiceSessionWire[];
}

function base(slug: string): string {
  return `/api/t/${encodeURIComponent(slug)}/v1`;
}

async function parseOrThrow<T>(res: Response, label: string): Promise<T> {
  if (!res.ok) {
    let body: unknown;
    try {
      body = await res.json();
    } catch {
      body = await res.text().catch(() => null);
    }
    throw new ApiError(res.status, body, `${label} → HTTP ${res.status}`);
  }
  return (await res.json()) as T;
}

export function spiceSessionsKey(slug: string, clientId: string): readonly unknown[] {
  return ['spice-sessions', slug, clientId] as const;
}

export async function fetchSpiceSessions(
  slug: string,
  clientId: string,
  signal?: AbortSignal,
): Promise<SpiceSessionsResponse> {
  const url = `${base(slug)}/clients/${encodeURIComponent(clientId)}/spice-sessions`;
  const res = await fetch(url, { method: 'GET', headers: { Accept: 'application/json' }, signal });
  return parseOrThrow<SpiceSessionsResponse>(res, `GET ${url}`);
}

export function useSpiceSessions(slug: string, clientId: string, opts: { enabled?: boolean } = {}) {
  const query = useQuery<SpiceSessionsResponse>({
    queryKey: spiceSessionsKey(slug, clientId),
    queryFn: ({ signal }) => fetchSpiceSessions(slug, clientId, signal),
    staleTime: 30_000,
    enabled: (opts.enabled ?? true) && slug !== '' && clientId !== '',
  });
  return {
    rows: query.data?.rows ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
  };
}
